import React, { useContext, useEffect } from 'react';
import { useRoutes, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import Home from './components/Shared/Home';
import ParticipantHome from './components/Shared/ParticipantHome';
import Login from "./components/User/Login";
import ForgotPassword from "./components/User/ForgotPassword";
import Dashboard from "./components/Shared/Dashboard";
import Studies from "./components/Study/Studies";
import SingleStudy from "./components/Study/SingleStudy";
import CreateStudy from "./components/Study/CreateStudy";
import Sites from "./components/Site/Sites";
import AddSites from "./components/Site/AddSites";
import SiteDetails from "./components/Site/SiteDetails";
import AddParticipantToSite from "./components/Participant/AddParticipantToSite";
import ParticipantDetails from "./components/Participant/ParticipantDetails";
import NotificationsPage from "./components/Notifications/NotificationsPage";
import CaregiverLogin from "./components/Caregivers/CaregiverLogin";
import CaregiverSessionGuard from "./components/Caregivers/CaregiverSessionGuard";
import CaregiverFirstLoginPin from "./components/Caregivers/CaregiverFirstLoginPin";
import CaregiverInviteAccept from "./components/Caregivers/CaregiverInviteAccept";
import CaregiverDashboard from "./components/Caregivers/CaregiverDashboard";
import CaregiverSurveyTaking from "./components/Caregivers/CaregiverSurveyTaking";
import ParticipantLogin from "./components/Participant/ParticipantLogin";
import DCQs from "./components/DCQ/DCQs";
import SingleDCQ from "./components/DCQ/SingleDCQ";
import UnknownError from "./components/Shared/UnknownError";
import NotFound from "./components/Shared/NotFound";
import SessionExpired from "./components/Shared/SessionExpired";
import RoleRequests from "./components/User/RoleRequests";
import PasswordReset from "./components/User/PasswordReset";
import Development from "./components/Development";
import Sitemap from "./components/Shared/Sitemap";
import CaregiverQrHarness from "./components/Caregivers/CaregiverQrHarness";
import ScheduleBuilderHarness from "./components/studybuilder/ScheduleBuilderHarness";
import EditSite from './components/Site/EditSite';
import UserManager from './components/User/UserManager';
import FileUpload from './components/Shared/FileUpload';
import ServerError from './components/Shared/ServerError';
import Survey from './components/Survey/Survey';
import UserProfile from './components/User/UserProfile';
import ProfilePage from './components/Profile/ProfilePage';
import EditStudy from './components/Study/EditStudy';
import AllSites from './components/Site/AllSites';
import DataVisualizationPage from './components/Shared/DataVisualization';
import CreateDCQ from './components/DCQ/CreateDCQ/CreateDCQ';
import ParticipantDashboard from "./components/Participant/ParticipantDashboard";
import SponsorSelector from "./components/Sponsor/SponsorSelector";
import ActivateAccount from './components/Shared/ActivateAccount';
import ParticipantDeactivation from './components/Participant/ParticipantDeactivation';
import ParticipantActivation from './components/Participant/ParticipantActivation';
import CompliancePage from './components/Study/CompliancePage';
import WorkInProgress from './components/Shared/WIP';
import ChangePassword from './components/User/ChangePassword';
import AssessmentPage from './components/Survey/AssessmentPage';
import FAQPage  from './components/Shared/FAQ';
import StudyBuilderMain from "./components/studybuilder/StudyBuilderMain";
import CreateStudyBuilder from "./components/studybuilder/CreateStudyBuilder";
import StudyLibraryDashboard from "./components/studybuilder/StudyLibraryDashboard";
import StudyBuilderOptions from "./components/studybuilder/StudyBuilderOptions";
import ScheduleBuilder from "./components/studybuilder/ScheduleBuilder";
import MFARecoveryRequest from "./components/Auth/MFARecoveryRequest";
import MFARecoveryVerify from "./components/Auth/MFARecoveryVerify";
import ApiKeyManagement from './components/Shared/ApiKey';
import StudyList from './components/studybuilder/StudyList';
import SurveyTaking from './components/Survey/SurveyTaking';
import GlobalLibrary from './components/studybuilder/GlobalLibraryDashboard';
import UserManagement from './pages/UserManagement';
import SuperAdminPanel from './pages/SuperAdminPanel';
import CompletedSurveyDetails from './components/Survey/CompletedSurveyDetails';
import DukeSliderDemo from './components/Shared/DukeSliderDemo';
import AssessmentBuilderMain from './components/AssessmentBuilder/AssessmentBuilderMain';
import ReviewQueuePage from './components/AssessmentBuilder/ReviewQueuePage';
import ReviewFrame from './components/AssessmentBuilder/ReviewFrame';
import ReviewHistoryPage from './components/AssessmentBuilder/ReviewHistoryPage';
import ArchivedAssessmentsPage from './pages/ArchivedAssessmentsPage';
import NotificationSubscriptions from './components/User/NotificationSubscriptions';

import { AuthContext } from './contexts/AuthContext';
import PasswordChangeGuard from './components/User/PasswordChangeGuard';
import { isParticipantDomain } from './utils/participantDomain';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, mfaSetupRequired, mfaRequired, userPermissions, permissionsLoaded } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Handle sponsor selection navigation for internal users
    // Check if permissions have loaded by verifying the object has keys
    const permissionsLoaded = userPermissions && Object.keys(userPermissions).length > 0;
    
    if (!loading && isAuthenticated && user?.is_internal && !mfaSetupRequired && !mfaRequired && permissionsLoaded) {
      const sponsorCount = userPermissions.sponsor_count || 0;
      const canSelectSponsor = userPermissions.can_select_sponsor === true;
      
      // Validate selectedSponsor - must be parseable JSON with an id
      let hasValidSponsor = false;
      try {
        const selectedSponsorStr = localStorage.getItem('selectedSponsor');
        if (selectedSponsorStr) {
          const selectedSponsor = JSON.parse(selectedSponsorStr);
          hasValidSponsor = !!(selectedSponsor && selectedSponsor.id);
        }
      } catch (e) {
        // Invalid JSON in localStorage - clear it
        console.warn('[PROTECTED ROUTE] Invalid sponsor data in localStorage, clearing:', e);
        localStorage.removeItem('selectedSponsor');
        hasValidSponsor = false;
      }
      
      console.debug('[PROTECTED ROUTE] Checking sponsor selection:', { 
        hasValidSponsor, 
        sponsorCount, 
        canSelectSponsor,
        pathname: location.pathname 
      });
      
      // Exempt certain paths from sponsor selection requirement
      const exemptPaths = ['/sponsor-selector', '/profile', '/change-password'];
      
      // Redirect to sponsor selector if:
      // 1. No valid sponsor is currently selected AND
      // 2. User has permission to select sponsors, or has no sponsors assigned
      //    (the selector renders the "No Sponsors Assigned" empty state) AND
      // 3. Not on an exempt path
      // This ensures users must select a sponsor context before accessing the app
      if (!hasValidSponsor && !exemptPaths.includes(location.pathname) && (sponsorCount === 0 || canSelectSponsor)) {
        console.debug('[PROTECTED ROUTE] User needs to select sponsor, navigating from:', location.pathname);
        navigate('/sponsor-selector');
      }
    }
  }, [isAuthenticated, loading, user, userPermissions, mfaSetupRequired, mfaRequired, navigate, location.pathname]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // If MFA is required, show a loading state and let MFAPrompt handle the modal
  // Don't redirect - this prevents the infinite loop
  if (mfaSetupRequired || mfaRequired) {
    console.debug('[PROTECTED ROUTE] MFA required, showing loading state:', { mfaSetupRequired, mfaRequired });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Setting up multi-factor authentication...</p>
        </div>
      </div>
    );
  }

  // Hold rendering until permissions have settled. Without this, role-gated
  // child pages render against the empty initial permissions object and briefly
  // flash "Access Denied" — most visibly right after a QA "view as role" reload.
  if (!permissionsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Wrap with password change guard to enforce password reset when needed
  return <PasswordChangeGuard>{children}</PasswordChangeGuard>;
};

const ParticipantRoute = ({ children }) => {
  const { isAuthenticated, loading, userType } = useContext(AuthContext);
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (userType !== 'participant') {
    return <Navigate to="/" />;
  }
  
  // Wrap with password change guard to enforce password reset when needed
  return <PasswordChangeGuard>{children}</PasswordChangeGuard>;
};

const InternalRoute = ({ children }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.is_internal === false) {
      console.log('[ROUTES] Non-internal user attempting to access internal route');
      navigate('/studies');
    }
  }, [user, navigate]);

  if (!user || user.is_internal === false) {
    return null;
  }

  // Wrap with password change guard to enforce password reset when needed
  return <PasswordChangeGuard>{children}</PasswordChangeGuard>;
};

// Only allow access to superusers and program admins
const AdminRoute = ({ children }) => {
  const { user } = useContext(AuthContext);
  const isUserManager = user?.is_superuser || user?.role === 'program_admin';
  
  if (!isUserManager) {
    return <Navigate to="/studies" />;
  }
  
  // Wrap with password change guard to enforce password reset when needed
  return <PasswordChangeGuard>{children}</PasswordChangeGuard>;
};

export function AppRoutes({ isDev }) {
  const { isAuthenticated, loading, user, userType, userPermissions } = useContext(AuthContext);

  if (loading) {
    return <div>Loading...</div>;
  }

  // Helper function to check if user has valid sponsor selected
  const hasValidSponsor = () => {
    try {
      const selectedSponsorStr = localStorage.getItem('selectedSponsor');
      if (!selectedSponsorStr) return false;
      const selectedSponsor = JSON.parse(selectedSponsorStr);
      return !!(selectedSponsor && selectedSponsor.id);
    } catch (e) {
      // Invalid JSON - clear it
      localStorage.removeItem('selectedSponsor');
      return false;
    }
  };

  // Check if permissions have loaded
  const permissionsLoaded = userPermissions && Object.keys(userPermissions).length > 0;
  
  // Determine root route redirect
  const getRootRedirect = () => {
    if (!isAuthenticated) {
      return isParticipantDomain() ? <ParticipantHome /> : <Home />;
    }
    
    if (userType === 'participant') {
      return <Navigate to="/participant-dashboard" />;
    }
    
    // For internal users, check sponsor selection
    if (user?.is_internal) {
      const validSponsor = hasValidSponsor();
      const sponsorCount = userPermissions?.sponsor_count || 0;
      
      console.debug('[ROOT ROUTE] Redirect decision:', {
        permissionsLoaded,
        validSponsor,
        sponsorCount,
        userPermissions
      });
      
      // If permissions loaded and user needs sponsor selection. Zero-sponsor
      // users also route there, to the "No Sponsors Assigned" empty state.
      if (permissionsLoaded && !validSponsor) {
        console.debug('[ROOT ROUTE] Redirecting to sponsor selector');
        return <Navigate to="/sponsor-selector" />;
      }
    }
    
    return <Navigate to="/studies" />;
  };

  const routes = [
    { 
      path: "/", 
      element: getRootRedirect()
    },
    { path: "/home", element: <Home /> },
    { path: "/home/:StudyId", element: <ParticipantHome /> },
    { 
      path: "/login", 
      element: isAuthenticated ? <Navigate to="/studies" /> : <Login />
    },
    { path: "/login/participant", element: <ParticipantLogin /> },
    { path: "/forgot-password", element: <ForgotPassword /> },
    { path: "/reset-password", element: <ForgotPassword /> },
    { path: "/reset-password/:uid/:token", element: <PasswordReset /> },
    { path: "/mfa/recovery/request", element: <MFARecoveryRequest /> },
    { path: "/mfa/recovery/verify/:token", element: <MFARecoveryVerify /> },
    { path: "/session-expired", element: <SessionExpired /> },
    { path: "/server-error", element: <ServerError /> },
    { path: "/unknown-error", element: <UnknownError /> },
    { path: "/change-password", element: <ProtectedRoute><ChangePassword /></ProtectedRoute> },
    { 
      path: "/participant-dashboard", 
      element: <ParticipantRoute><ParticipantDashboard /></ParticipantRoute> 
    },
    { 
      path: "/assessment/:assessmentId", 
      element: <ParticipantRoute><AssessmentPage /></ParticipantRoute> 
    },
    { 
      path: "/survey-taking/:assignmentId", 
      element: <ParticipantRoute><SurveyTaking /></ParticipantRoute> 
    },
    { 
      path: "/dashboard", 
      element: <ProtectedRoute><Dashboard /></ProtectedRoute> 
    },
    { 
      path: "/studies", 
      element: <ProtectedRoute><Studies /></ProtectedRoute> 
    },
    { 
      path: "/studybuilder", 
      element: <ProtectedRoute><InternalRoute><StudyBuilderMain /></InternalRoute></ProtectedRoute> 
    },
    { 
      path: "/studybuilder/create", 
      element: <ProtectedRoute><InternalRoute><CreateStudyBuilder /></InternalRoute></ProtectedRoute> 
    },
    { 
      path: "/studybuilder/:studyName", 
      element: <ProtectedRoute><InternalRoute><StudyBuilderOptions /></InternalRoute></ProtectedRoute> 
    },
    { 
      path: "/studybuilder/:studyName/library", 
      element: <ProtectedRoute><InternalRoute><StudyLibraryDashboard /></InternalRoute></ProtectedRoute> 
    },
    { 
      path: "/studybuilder/:studyName/schedule", 
      element: <ProtectedRoute><InternalRoute><ScheduleBuilder /></InternalRoute></ProtectedRoute> 
    },
    {
      path: "/studybuilder/drafts",
      element: (
        <ProtectedRoute>
          <InternalRoute>
            <StudyList />
          </InternalRoute>
        </ProtectedRoute>
      ),
    },
    {
      path: "/studybuilder/review/:studyName",
      element: (
        <ProtectedRoute>
          <InternalRoute>
            <ReviewFrame entityType="study" />
          </InternalRoute>
        </ProtectedRoute>
      ),
    },
    { 
      path: "/studybuilder/global-library", 
      element: <ProtectedRoute><InternalRoute><GlobalLibrary /></InternalRoute></ProtectedRoute> 
    },
    {
      path: "/assessment-builder",
      element: <ProtectedRoute><AssessmentBuilderMain /></ProtectedRoute>
    },
    {
      path: "/assessment-builder/review-queue",
      element: <ProtectedRoute><ReviewQueuePage /></ProtectedRoute>
    },
    {
      path: "/review-history/:entityType/:objectId",
      element: <ProtectedRoute><ReviewHistoryPage /></ProtectedRoute>
    },
    {
      path: "/assessment-builder/review/:assessmentId",
      element: <ProtectedRoute><ReviewFrame entityType="assessment" /></ProtectedRoute>
    },
    {
      path: "/assessment-builder/view/:assessmentId",
      element: <ProtectedRoute><AssessmentBuilderMain readOnlyView /></ProtectedRoute>
    },
    {
      path: "/assessment-builder/:assessmentId",
      element: <ProtectedRoute><AssessmentBuilderMain /></ProtectedRoute>
    },
    { 
      path: "/archived-assessments", 
      element: <ProtectedRoute><ArchivedAssessmentsPage /></ProtectedRoute> 
    },
    { 
      path: "/sponsor-selector", 
      element: <ProtectedRoute>
        {user && user.is_internal ? <SponsorSelector /> : <Navigate to="/studies" />}
      </ProtectedRoute> 
    },
    { 
      path: "/sites", 
      element: <ProtectedRoute><AllSites /></ProtectedRoute> 
    },
    { 
      path: "/studies/:studyID/sites/:siteID/participant/:participantID/deactivate", 
      element: <ProtectedRoute><ParticipantDeactivation /></ProtectedRoute> 
    },
    { 
      path: "/studies/:studyID/sites/:siteID/participant/:participantID/activate", 
      element: <ProtectedRoute><ParticipantActivation /></ProtectedRoute> 
    },
    { 
      path: "/studies/:studyID/sites/:siteId/participant/:participantID/create_dcq", 
      element: <ProtectedRoute><CreateDCQ /></ProtectedRoute>
    },    
    { 
      path: "/studies/:studyID/compliance", 
      element: <ProtectedRoute><CompliancePage /></ProtectedRoute> 
    },
    { 
      path: "/create-new-study", 
      element: <ProtectedRoute><CreateStudy /></ProtectedRoute> 
    },
    { 
      path: "/studies/:studyID", 
      element: <ProtectedRoute><SingleStudy /></ProtectedRoute> 
    },
    { 
      path: "/studies/:studyID/sites", 
      element: <ProtectedRoute><Sites /></ProtectedRoute> 
    },
    { 
      path: "/studies/:studyID/add-site/", 
      element: <ProtectedRoute><AddSites /></ProtectedRoute> 
    },
    { 
      path: "/studies/:studyID/sites/:siteID/", 
      element: <ProtectedRoute><SiteDetails /></ProtectedRoute> 
    },
    { 
      path: "/studies/:studyID/sites/:siteID/edit", 
      element: <ProtectedRoute><EditSite /></ProtectedRoute> 
    },
    { 
      path: "/studies/:studyID/sites/:siteId/participants/add-participant", 
      element: <ProtectedRoute><AddParticipantToSite /></ProtectedRoute> 
    },
    {
      path: "/studies/:studyId/sites/:siteId/participant/:participantID/",
      element: <ProtectedRoute><ParticipantDetails /></ProtectedRoute>
    },
    // Caregiver-facing routes (FECOA-1320). No ProtectedRoute wrap because
    // these pages handle their own JWT (caregiver tokens, not staff tokens).
    {
      path: "/caregiver-login",
      element: <CaregiverLogin />,
    },
    {
      path: "/caregiver/first-login",
      element: <CaregiverSessionGuard><CaregiverFirstLoginPin /></CaregiverSessionGuard>,
    },
    {
      path: "/caregiver/invite",
      element: <CaregiverInviteAccept />,
    },
    {
      path: "/caregiver/dashboard",
      element: <CaregiverSessionGuard><CaregiverDashboard /></CaregiverSessionGuard>,
    },
    {
      path: "/caregiver/survey/:assignmentId",
      element: <CaregiverSessionGuard><CaregiverSurveyTaking /></CaregiverSessionGuard>,
    },
    {
      path: "/studies/:studyID/edit",
      element: <ProtectedRoute><EditStudy /></ProtectedRoute> 
    },
    { 
      path: "/user-management", 
      element: <ProtectedRoute><AdminRoute><UserManagement /></AdminRoute></ProtectedRoute> 
    },
    { 
      path: "/superadmin", 
      element: <ProtectedRoute><SuperAdminPanel /></ProtectedRoute> 
    },
    { 
      path: "/data-visualization", 
      element: <ProtectedRoute><DataVisualizationPage /></ProtectedRoute> 
    },
    { 
      path: "/surveys", 
      element: <ProtectedRoute><Survey /></ProtectedRoute> 
    },
    { 
      path: "/studies/:studyID/upload", 
      element: <ProtectedRoute><FileUpload /></ProtectedRoute> 
    },
    { 
      path: "/studies/:studyID/dcq/:dcqId/", 
      element: <ProtectedRoute><SingleDCQ /></ProtectedRoute> 
    },    
    { 
      path: "/dcq/", 
      element: <ProtectedRoute><DCQs /></ProtectedRoute> 
    },
        { 
      path: "/dcq/create/", 
      element: <ProtectedRoute><CreateDCQ /></ProtectedRoute> 
    },
    { 
      path: "/rolerequests", 
      element: <ProtectedRoute><RoleRequests /></ProtectedRoute> 
    },
    { 
      path: "/faq", 
      element: <ProtectedRoute><FAQPage /></ProtectedRoute> 
    },
    { 
      path: "/duke-slider", 
      element: <ProtectedRoute><DukeSliderDemo /></ProtectedRoute> 
    },
    { 
      path: "/profile", 
      element: <ProtectedRoute><ProfilePage /></ProtectedRoute> 
    },
    { 
      path: "/profile/notifications", 
      element: <ProtectedRoute><NotificationSubscriptions /></ProtectedRoute> 
    },
    {
      path: "/change-password",
      element: <ProtectedRoute><ChangePassword /></ProtectedRoute>
    },
    {
      path: "/api-key",
      element: <ProtectedRoute><ApiKeyManagement /></ProtectedRoute>
    },
    { path: "/wip", element: <WorkInProgress /> },
    { 
      path: "/auth/activate/:uid/:token", 
      element: <ActivateAccount /> 
    },
    {
      path: "/notifications",
      element: <ProtectedRoute><NotificationsPage /></ProtectedRoute>
    },
    {
      path: "/completed-surveys/:surveyId",
      element: <ProtectedRoute><CompletedSurveyDetails /></ProtectedRoute>
    },
    { 
      path: "/studies/:studyId/sites/:siteId/participant/:participantID/completed-surveys/:surveyId", 
      element: <ProtectedRoute><CompletedSurveyDetails /></ProtectedRoute> 
    },
    // redirect /contact-us an /learn-more to home page
    { 
      path: "/contact-us", 
      element: <Navigate to="/" />
    },
    { 
      path: "/learn-more", 
      element: <Navigate to="/" />
    },

    // Debug route
    { 
      path: "/participant/:participantID/*", 
      element: (() => {
        console.debug('Debug Route:', window.location.pathname);
        return null;
      })()
    },
    ...(isDev ? [
      { path: "/sitemap", element: <Sitemap /> },
      { path: "/development", element: <Development /> }
    ] : []),
    // Dev-build-only component harness for the caregiver setup-QR Playwright
    // suite. Gated on the Vite DEV flag (not the superuser `isDev`) so it needs
    // no auth and is stripped from production bundles.
    ...(import.meta.env.DEV ? [
      { path: "/e2e/caregiver-qr", element: <CaregiverQrHarness /> },
      { path: "/e2e/schedule-builder", element: <ScheduleBuilderHarness /> }
    ] : []),
    { path: "*", element: <NotFound /> }
  ];

  return useRoutes(routes);
}

export default AppRoutes;